import { Transition } from "@headlessui/react"
import { Form, Head } from "@inertiajs/react"
import { Key, Mail, Shield } from "lucide-react"

import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsSecurityPath } from "@/routes"
import type { BreadcrumbItem, User } from "@/types"

interface SecurityProps {
  user: User & { auth_method: "password" | "otp" }
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Security settings",
    href: settingsSecurityPath(),
  },
]

export default function Security({ user }: SecurityProps) {
  const isPasswordAuth = user.auth_method === "password"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <div className="p-6">
        <SettingsLayout>
          <div className="space-y-6">
            <HeadingSmall
              title="Authentication method"
              description="Choose how you want to sign in to your account"
            />

            <Form
              method="put"
              action={settingsSecurityPath()}
              options={{
                preserveScroll: true,
              }}
              className="space-y-6"
            >
              {({ processing, recentlySuccessful }) => (
                <>
                  <div className="grid gap-4">
                    <Label className="text-base">Sign-in method</Label>

                    <div className="grid gap-3">
                      <AuthMethodOption
                        name="auth_method"
                        value="password"
                        checked={isPasswordAuth}
                        icon={<Key className="h-5 w-5" />}
                        title="Password"
                        description="Sign in with your email and password"
                      />

                      <AuthMethodOption
                        name="auth_method"
                        value="otp"
                        checked={!isPasswordAuth}
                        icon={<Mail className="h-5 w-5" />}
                        title="Email code (OTP)"
                        description="Receive a one-time code via email each time you sign in"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <Button disabled={processing}>
                      <Shield className="mr-2 h-4 w-4" />
                      Save preference
                    </Button>

                    <Transition
                      show={recentlySuccessful}
                      enter="transition ease-in-out"
                      enterFrom="opacity-0"
                      leave="transition ease-in-out"
                      leaveTo="opacity-0"
                    >
                      <p className="text-sm text-neutral-600">Saved</p>
                    </Transition>
                  </div>
                </>
              )}
            </Form>
          </div>
        </SettingsLayout>
      </div>
    </AppLayout>
  )
}

interface AuthMethodOptionProps {
  name: string
  value: string
  checked: boolean
  icon: React.ReactNode
  title: string
  description: string
}

function AuthMethodOption({
  name,
  value,
  checked,
  icon,
  title,
  description,
}: AuthMethodOptionProps) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition-colors ${
        checked
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/50"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        defaultChecked={checked}
        className="sr-only"
      />
      <div
        className={`mt-0.5 ${checked ? "text-primary" : "text-muted-foreground"}`}
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className={`font-medium ${checked ? "text-primary" : ""}`}>
          {title}
        </div>
        <div className="text-muted-foreground text-sm">{description}</div>
      </div>
      <div
        className={`mt-1 h-4 w-4 rounded-full border-2 ${
          checked ? "border-primary bg-primary" : "border-muted-foreground"
        }`}
      >
        {checked && (
          <div className="flex h-full items-center justify-center">
            <div className="h-1.5 w-1.5 rounded-full bg-white" />
          </div>
        )}
      </div>
    </label>
  )
}
